<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Inventory Movements</h1>
				<p class="text-muted mb-0">
					The ledger: every receipt, subdivision, checkout, return, waste,
					disposal and adjustment, newest first. Entries are never changed or
					removed.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div class="card">
			<div class="card-body">
				<div class="row g-2 align-items-end mb-3">
					<div class="col-12 col-md-3">
						<label class="form-label" for="movementKindFilter">Kind</label>
						<select
							id="movementKindFilter"
							v-model="kindFilter"
							class="form-select"
						>
							<option value="">All kinds</option>
							<option
								v-for="option in KIND_OPTIONS"
								:key="option.value"
								:value="option.value"
							>
								{{ option.label }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-3">
						<label class="form-label" for="movementProductFilter">
							Product
						</label>
						<input
							id="movementProductFilter"
							v-model="productFilter"
							type="search"
							class="form-control"
							placeholder="Filter by product name"
						/>
					</div>
					<div class="col-12 col-md-3">
						<label class="form-label" for="movementLocationFilter">
							Storage location
						</label>
						<select
							id="movementLocationFilter"
							v-model="locationFilter"
							class="form-select"
						>
							<option :value="null">All locations</option>
							<option
								v-for="location in locationOptions"
								:key="location.id"
								:value="location.id"
							>
								{{ location.path }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-3">
						<label class="form-label" for="movementLimit">Show</label>
						<select id="movementLimit" v-model="limit" class="form-select">
							<option
								v-for="option in LIMIT_OPTIONS"
								:key="option"
								:value="option"
							>
								{{ option }} movements
							</option>
						</select>
					</div>
				</div>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="movements.length === 0"
					class="text-center py-4 text-muted"
				>
					No movements yet. Receiving a product writes the ledger's first entry.
				</div>

				<div
					v-else-if="visibleMovements.length === 0"
					class="text-center py-4 text-muted"
				>
					No movements match the filters.
				</div>

				<template v-else>
					<div class="table-responsive">
						<table class="table table-striped table-hover align-middle">
							<thead class="table-light">
								<tr>
									<th scope="col">Time</th>
									<th scope="col">Product</th>
									<th scope="col">Storage location</th>
									<th scope="col">Kind</th>
									<th scope="col">Amount</th>
									<th scope="col">Reason</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="movement in visibleMovements" :key="movement.id">
									<td class="text-nowrap">
										<TimeRelativeWithTooltip :time="movement.occurred" />
									</td>
									<td class="fw-semibold">{{ movement.product_name }}</td>
									<td>
										{{
											movement.location_path ?? movement.location_name ?? "—"
										}}
									</td>
									<td>
										<span
											class="badge rounded-pill"
											:class="kindClass(movement.kind)"
										>
											{{ kindLabel(movement.kind) }}
										</span>
									</td>
									<td class="text-nowrap">
										<LocalizedNumber :value="movement.amount" />
										<span v-if="unitFor(movement)" class="ms-1 text-muted">
											{{ unitFor(movement) }}
										</span>
									</td>
									<td class="text-break">{{ movement.reason ?? "—" }}</td>
								</tr>
							</tbody>
						</table>
					</div>
					<p class="text-muted small mb-0">
						Showing {{ visibleMovements.length }} of
						{{ filteredMovements.length }} matching movements, of
						{{ movements.length }} in the ledger.
					</p>
				</template>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import LocalizedNumber from "@/components/LocalizedNumber.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	InventoryMovement,
	InventoryMovementKind,
	InventoryUnit,
} from "@/type/api";

// The ledger's kinds, in the design's order (doc/design/mvp/inventory.md,
// "Objects this module keeps").
const KIND_OPTIONS: { value: InventoryMovementKind; label: string }[] = [
	{ value: "receive", label: "Received" },
	{ value: "subdivide", label: "Subdivided" },
	{ value: "checkout", label: "Checked out" },
	{ value: "return", label: "Returned" },
	{ value: "waste", label: "Waste" },
	{ value: "dispose", label: "Disposed" },
	{ value: "adjust", label: "Adjusted" },
];

const KIND_LABELS = new Map<InventoryMovementKind, string>(
	KIND_OPTIONS.map((option) => [option.value, option.label] as const),
);

// A badge reads the direction the stock moved: product in, product out, or a
// change that is neither.
const INFLOW_KINDS: InventoryMovementKind[] = ["receive", "return"];
const OUTFLOW_KINDS: InventoryMovementKind[] = ["checkout", "waste", "dispose"];

const LIMIT_OPTIONS = [25, 50, 100, 250];

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const movements = ref<InventoryMovement[]>([]);
const units = ref<InventoryUnit[]>([]);
const loading = ref(false);
const error = ref("");

// An error is both the notification and the page state: the toast tells the
// operator, and the inline strip keeps the reason on screen after it dismisses.
function fail(title: string, e: any, fallback: string) {
	const message = e?.message || fallback;
	error.value = message;
	showNotification(title, message);
}

const kindFilter = ref<InventoryMovementKind | "">("");
const productFilter = ref("");
const locationFilter = ref<number | null>(null);
const limit = ref(100);

// Newest first is the ledger's own reading order, whatever order the server
// returned, with the id breaking ties inside one instant.
const orderedMovements = computed(() =>
	[...movements.value].sort((a, b) => {
		const byTime = Date.parse(b.occurred) - Date.parse(a.occurred);
		return byTime !== 0 ? byTime : b.id - a.id;
	}),
);

const locationOptions = computed(() => {
	const byID = new Map<number, string>();
	for (const movement of movements.value) {
		if (movement.storage_location_id === null) {
			continue;
		}
		const path = movement.location_path ?? movement.location_name;
		if (path === null) {
			continue;
		}
		byID.set(movement.storage_location_id, path);
	}
	return [...byID.entries()]
		.map(([id, path]) => ({ id, path }))
		.sort((a, b) => a.path.localeCompare(b.path));
});

const filteredMovements = computed(() => {
	const needle = productFilter.value.trim().toLowerCase();
	return orderedMovements.value.filter((movement) => {
		if (kindFilter.value !== "" && movement.kind !== kindFilter.value) {
			return false;
		}
		if (
			locationFilter.value !== null &&
			movement.storage_location_id !== locationFilter.value
		) {
			return false;
		}
		if (
			needle !== "" &&
			!movement.product_name.toLowerCase().includes(needle)
		) {
			return false;
		}
		return true;
	});
});

const visibleMovements = computed(() =>
	filteredMovements.value.slice(0, limit.value),
);

// The ledger carries no unit of its own, so the amount is labelled with the
// product's base unit where the district has set one.
const unitsByProduct = computed(() => {
	const byProduct = new Map<string, string>();
	for (const unit of units.value) {
		if (unit.unit.trim() !== "") {
			byProduct.set(unit.product_catalog_id, unit.unit.trim());
		}
	}
	return byProduct;
});

function unitFor(movement: InventoryMovement): string | null {
	return unitsByProduct.value.get(movement.product_catalog_id) ?? null;
}

function kindLabel(kind: InventoryMovementKind): string {
	return KIND_LABELS.get(kind) ?? kind;
}

function kindClass(kind: InventoryMovementKind): string {
	if (INFLOW_KINDS.includes(kind)) return "text-bg-success";
	if (OUTFLOW_KINDS.includes(kind)) return "text-bg-danger";
	return "text-bg-light border";
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		// Both collections through the generic QUERY endpoints (the legacy GET
		// lists' replacement, doc/in-progress/query.md §11 Phase 4): the movements
		// are the whole ledger and the units are the lookup the kinds are named
		// from, so both are walked whole as the un-paged fetches were.
		const [movementPage, unitPage] = await Promise.all([
			apiClient.JSONQueryAll<InventoryMovement>("/api/inventory/movement"),
			apiClient.JSONQueryAll<InventoryUnit>("/api/inventory/unit"),
		]);
		movements.value = movementPage.items;
		units.value = unitPage.items;
	} catch (e: any) {
		fail("Failed to load the movement ledger", e, "Please reload the page.");
	} finally {
		loading.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
